import { createFeatureSelector, createSelector } from '@ngrx/store';
import { DocumentState } from './document.reducer';

/**
 * Document Selectors
 */

// Feature selector
export const selectDocumentState = createFeatureSelector<DocumentState>('documents');

// Documents list selectors
export const selectDocuments = createSelector(
  selectDocumentState,
  (state) => state.documents
);

export const selectDocumentsTotal = createSelector(
  selectDocumentState,
  (state) => state.total
);

export const selectDocumentsPage = createSelector(
  selectDocumentState,
  (state) => state.page
);

export const selectDocumentsPageSize = createSelector(
  selectDocumentState,
  (state) => state.page_size
);

export const selectDocumentsTotalPages = createSelector(
  selectDocumentState,
  (state) => state.total_pages
);

export const selectDocumentsHasNext = createSelector(
  selectDocumentState,
  (state) => state.has_next
);

export const selectDocumentsHasPrev = createSelector(
  selectDocumentState,
  (state) => state.has_prev
);

export const selectDocumentsPagination = createSelector(
  selectDocumentState,
  (state) => ({
    page: state.page,
    page_size: state.page_size,
    total: state.total,
    total_pages: state.total_pages,
    has_next: state.has_next,
    has_prev: state.has_prev
  })
);

// Filters selector
export const selectDocumentsFilters = createSelector(
  selectDocumentState,
  (state) => state.filters
);

// Selected document selector
export const selectSelectedDocument = createSelector(
  selectDocumentState,
  (state) => state.selectedDocument
);

// Upload queue selectors
export const selectUploadQueue = createSelector(
  selectDocumentState,
  (state) => state.uploadQueue
);

export const selectActiveUploads = createSelector(
  selectUploadQueue,
  (queue) => queue.filter(item => item.status === 'uploading' || item.status === 'pending')
);

export const selectCompletedUploads = createSelector(
  selectUploadQueue,
  (queue) => queue.filter(item => item.status === 'completed')
);

export const selectFailedUploads = createSelector(
  selectUploadQueue,
  (queue) => queue.filter(item => item.status === 'failed')
);

export const selectUploadProgress = createSelector(
  selectUploadQueue,
  (queue) => {
    if (queue.length === 0) return 0;
    const totalProgress = queue.reduce((sum, item) => sum + item.progress, 0);
    return Math.round(totalProgress / queue.length);
  }
);

// Loading state selectors
export const selectDocumentsLoading = createSelector(
  selectDocumentState,
  (state) => state.loading
);

export const selectDocumentLoading = createSelector(
  selectDocumentState,
  (state) => state.loadingDocument
);

export const selectUploading = createSelector(
  selectDocumentState,
  (state) => state.uploading
);

export const selectDeleting = createSelector(
  selectDocumentState,
  (state) => state.deleting
);

// Error state selectors
export const selectDocumentsError = createSelector(
  selectDocumentState,
  (state) => state.error
);

export const selectUploadError = createSelector(
  selectDocumentState,
  (state) => state.uploadError
);

export const selectDeleteError = createSelector(
  selectDocumentState,
  (state) => state.deleteError
);

// Computed selectors
export const selectHasDocuments = createSelector(
  selectDocuments,
  (documents) => documents.length > 0
);

export const selectIsUploading = createSelector(
  selectActiveUploads,
  (uploads) => uploads.length > 0
);

export const selectDocumentById = (documentId: string) => createSelector(
  selectDocuments,
  (documents) => documents.find(doc => doc._id === documentId)
);

// Document detail selectors
export const selectCurrentDocument = createSelector(
  selectDocumentState,
  (state) => state.currentDocument
);

export const selectDocumentDetailLoading = createSelector(
  selectDocumentState,
  (state) => state.loadingDocumentDetail
);

export const selectDocumentDetailError = createSelector(
  selectDocumentState,
  (state) => state.documentDetailError
);

